import { defineStore } from 'pinia'

export type ComponentConfig = {
  color: string
  embossing?: string
  embossingColor?: string
}

type SettingsMap = Record<string, ComponentConfig>

const LOCAL_STORAGE_KEY = "componentSettings"

// --- LocalStorage helpers ---

function loadSettings(): SettingsMap {
  const raw = localStorage.getItem(LOCAL_STORAGE_KEY)
  return raw ? JSON.parse(raw) : {}
}

function saveSettings(settings: SettingsMap) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(settings))
}

export function setComponentSettings(id: number, config: Partial<ComponentConfig>) {
  const all = loadSettings()
  const key = id.toString()
  all[key] = { ...(all[key] || {}), ...config }
  saveSettings(all)
}

export function getComponentSettings(id: number): ComponentConfig | undefined {
  const all = loadSettings()
  return all[id.toString()]
}

export function deleteComponentSettings(id: number) {
  const all = loadSettings()
  const key = id.toString()
  if (key in all) {
    delete all[key]
    saveSettings(all)
  }
}

export function getAllComponentIDs(): number[] {
  const all = loadSettings()
  return Object.keys(all).map(k => Number(k))
}

export function clearAllComponentSettings() {
  localStorage.removeItem(LOCAL_STORAGE_KEY)
}

// --- Pinia store for activeComponentID ---

export const useComponentSettingStore = defineStore('componentSettings', {
  state: () => ({
    activeComponentID: null as number | null,
  }),
  actions: {
    getActiveID() {
      return this.activeComponentID;
    },
    setActiveID(id: number) {
      this.activeComponentID = id
    },
    saveConfig(config: Partial<ComponentConfig>) {
      if (this.activeComponentID !== null) {
        setComponentSettings(this.activeComponentID, config)
      }
    },
    loadConfig(): ComponentConfig | undefined {
      if (this.activeComponentID !== null) {
        return getComponentSettings(this.activeComponentID)
      }
      return undefined
    },
    deleteConfigForActive() {
      if (this.activeComponentID !== null) {
        deleteComponentSettings(this.activeComponentID)
      }
    },
    getAllComponentSettings(): [number, ComponentConfig][] {
      const all = loadSettings()
      return Object.entries(all).map(([key, value]) => [Number(key), value])
    },
    getAllIDs(): number[] {
      return getAllComponentIDs()
    },
    clearAll() {
      clearAllComponentSettings()
    },
  },
})
